import {Directive, ElementRef, Injector, input, Renderer2} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {distinctUntilChanged, Observable, switchMap} from "rxjs";
import {map} from "rxjs/operators";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";

const debug = initDebugger('VisDersomIkkeScrolletTilBunnDirektiv');

/**
 * Viser elementet direktivet anvendes på dersom angitt scrollkontainer ikke er scrollet helt til bunn.
 *
 * Bruk:
 * ```
 * <mat-dialog-content #skjemaKontainer>...</mat-dialog-content>
 * <div [visDersomIkkeScrolletTilBunn]="skjemaKontainer">Denne vises kun når man kan scrolle mer ned</div>
 * ```
 */
@Directive({
    selector: '[visDersomIkkeScrolletTilBunn]',
    standalone: true
})
export class VisDersomIkkeScrolletTilBunnDirektiv {

    /** Scrollkontaineren vi reagerer på */
    scrollkontainer = input.required<HTMLElement>({alias: 'visDersomIkkeScrolletTilBunn'});
    /** Dersom angitt settes denne stilen-klassen når på elementet direktivet er anvendt på når bunnen av scrollkontaineren ikke er synlig,
     * dersom ikke angitt sette style.display til none */
    klasseNårSynlig = input<string | null>(null, {alias: 'klasseNaarSynlig'});

    constructor(private el: ElementRef<HTMLElement>, private renderer: Renderer2, private injektor: Injector) {
        toObservable(this.scrollkontainer).pipe(
          switchMap(scrollkontainer => this.leggTilScrollKontainerBunnTriggerElement(scrollkontainer)),
          switchMap(triggerElement => intersectionObserverSomObservable(triggerElement, {threshold: [0.99]})),
          map(observasjon => !observasjon.isIntersecting),
          distinctUntilChanged(),
          takeUntilDestroyed()
        ).subscribe(skalVises => this.settSynlighet(skalVises));
    }

    //private hjelpemetoder

    private leggTilScrollKontainerBunnTriggerElement(scrollkontainer: HTMLElement): Observable<HTMLElement> {
        // Vi legger til et lite, usynlig element helt i bunnen av scrollkontaineren.
        // Når dette elementet er synlig (intersecting), betyr det at vi har scrollet til bunns.
        const trigger = this.renderer.createElement('div');
        //elementet må være synlig (kan ikke bruker display:none) - men gjør det i praksis usynlig her
        this.renderer.setStyle(trigger, 'display', '1px');
        this.renderer.setStyle(trigger, 'width', '1px');
        this.renderer.setStyle(trigger, 'marginTop', '-1px');

        return this.leggTilNyttSisteBarn(scrollkontainer, trigger);
    }

    private leggTilNyttSisteBarn(forelder: HTMLElement, nyttBarn: HTMLElement): Observable<HTMLElement> {
        return new Observable<HTMLElement>(subscriber => {
            this.renderer.appendChild(forelder, nyttBarn);
            subscriber.next(nyttBarn);
            return () => {
                if (nyttBarn.parentNode) {
                    this.renderer.removeChild(forelder, nyttBarn);
                }
            }
        });
    }

    private settSynlighet(skalVises: boolean) {
        debug('settSynlighet: %s', skalVises);
        if (skalVises) {
            if (erIkkeNil(this.klasseNårSynlig()))
                this.renderer.addClass(this.el.nativeElement, this.klasseNårSynlig()!);
            else
                this.renderer.setStyle(this.el.nativeElement, 'display', null);
        } else {
            if (erIkkeNil(this.klasseNårSynlig()))
                this.renderer.removeClass(this.el.nativeElement, this.klasseNårSynlig()!);
            else
                this.renderer.setStyle(this.el.nativeElement, 'display', 'none');
        }
    }
}


/**
 * Lager en ny {@link IntersectionObserver} knyttet til angitt element med konfigurasjon,
 * returnerer en observable som leverer {@link IntersectionObserverEntry} returnert av opprettet IntersectionObserver
 */
export function intersectionObserverSomObservable(element: HTMLElement, konfigurasjon?: IntersectionObserverInit): Observable<IntersectionObserverEntry> {
    return new Observable<IntersectionObserverEntry>(subscriber => {
        const observer = new IntersectionObserver(
          observasjoner => observasjoner.forEach(entry => subscriber.next(entry)),
          konfigurasjon);

        observer.observe(element);

        return () => observer.disconnect();
    });
}
